import { PLACE_ATTRIBUTE } from "./place.js";

/**
 * A PRESS ON A STUDY BUTTON (2026-11-04; the handoff's "Button shake and
 * chromatic fray", production defaults). The stylesheet's press run keys on
 * `data-press-run` rather than `:active`, because `:active` ends when the
 * pointer lifts — a short click would cut the 340ms run and hand the
 * element back to its hover rule, restarting the hover — and because a press
 * must replace a hover run in progress. One document-level listener sets
 * the attribute on the enabled button under a pointer down, or under Enter
 * or Space, alternating two values so a second press restarts the run, and
 * clears it when the pointer leaves the button or its focus moves on; a
 * press on a disabled control, or anywhere outside the study, sets
 * nothing. The attribute is presentational: nothing reads it but the
 * stylesheet, and no action waits on it.
 */
export const PRESS_ATTRIBUTE = "data-press-run";

function inTheStudy(doc: Document): boolean {
  return doc.documentElement.getAttribute(PLACE_ATTRIBUTE) === "study";
}

function pressedButton(target: EventTarget | null): HTMLButtonElement | null {
  if (!(target instanceof Element)) return null;
  const button = target.closest("button");
  if (button === null || button.disabled) return null;
  return button;
}

/** Marks a press: the attribute alternates between its two values so each press restarts the stylesheet's run. */
export function markPress(button: Element): void {
  button.setAttribute(PRESS_ATTRIBUTE, button.getAttribute(PRESS_ATTRIBUTE) === "a" ? "b" : "a");
}

/** Installs the listeners on a document; returns the function that removes them. */
export function installPressRuns(doc: Document = globalThis.document): () => void {
  const onPointerDown = (event: PointerEvent): void => {
    if (!inTheStudy(doc)) return;
    const button = pressedButton(event.target);
    if (button !== null) markPress(button);
  };
  const onKeyDown = (event: KeyboardEvent): void => {
    if (!inTheStudy(doc) || event.repeat || (event.key !== "Enter" && event.key !== " ")) return;
    const button = pressedButton(event.target);
    if (button !== null && button === event.target) markPress(button);
  };
  const clear = (event: Event): void => {
    // A finger lifts and leaves at once: its press keeps its mark, so the run plays whole; the next press restarts it.
    if (event.type === "pointerleave") {
      const pointerType = (event as Partial<PointerEvent>).pointerType;
      if (pointerType !== undefined && pointerType !== "" && pointerType !== "mouse") return;
    }
    const target = event.target;
    if (target instanceof Element && target.hasAttribute(PRESS_ATTRIBUTE)) target.removeAttribute(PRESS_ATTRIBUTE);
  };
  // All four in the capture phase: a React handler that stops propagation (a card's doors, a link mark)
  // stops it at React's root, below the document, and the two leaving events do not bubble at all.
  doc.addEventListener("pointerdown", onPointerDown, true);
  doc.addEventListener("keydown", onKeyDown, true);
  doc.addEventListener("pointerleave", clear, true);
  doc.addEventListener("blur", clear, true);
  return () => {
    doc.removeEventListener("pointerdown", onPointerDown, true);
    doc.removeEventListener("keydown", onKeyDown, true);
    doc.removeEventListener("pointerleave", clear, true);
    doc.removeEventListener("blur", clear, true);
  };
}
